import { ArrowLeft, ArrowRight, BookOpenCheck, Clock3, Loader2, Map, Target } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { getLatestAiGrowthPlan, getLearningPath, getStudentGrowthMe } from '../../api.js'
import { useAuth } from '../../auth-context.js'
import { useLanguage } from '../../contexts/LanguageContext.jsx'
import { buildStudentRoadmap, getRoadmapSummary } from '../../student-roadmap.js'
import { getEffectiveGrowthPlan, hasEffectiveGrowthData } from '../../student-growth-state.js'

const DEFAULT_PATH_ID = 'java-backend'

function emptyPath(id = DEFAULT_PATH_ID) {
  return {
    id,
    slug: id,
    name: id === DEFAULT_PATH_ID ? 'Java 后端开发路线' : '学习路线',
    category: '后端开发',
    description: '从基础语法、数据库、Spring Boot 到项目实战，形成可执行、可复盘、可写进简历的学习路径。',
    targetJob: 'Java 后端开发工程师',
    difficulty: '入门到就业',
    estimatedDays: 120,
    progress: 0,
    totalNodes: 0,
    completedNodes: 0,
    todayTask: null,
    stages: [],
  }
}

function typeLabel(type, t) {
  return {
    video: t('learningRoute.typeVideo'),
    document: t('learningRoute.typeDocument'),
    practice: t('learningRoute.typePractice'),
    project: t('learningRoute.typeProject'),
    review: t('learningRoute.typeReview'),
  }[type] || t('learningRoute.typeLearning')
}

function nodeStatus(node, t) {
  return node.completed ? t('learningRoute.nodeDone') : t('learningRoute.nodeNotStarted')
}

function normalizePath(payload, id) {
  const path = payload?.path && typeof payload.path === 'object' ? payload.path : emptyPath(id)
  return {
    ...emptyPath(id),
    ...path,
    stages: Array.isArray(path.stages) ? path.stages : [],
  }
}

function normalizePlanPath(payload) {
  if (!hasEffectiveGrowthData(payload)) return null

  const plan = getEffectiveGrowthPlan(payload)
  const roadmap = buildStudentRoadmap(plan)
  const summary = getRoadmapSummary(roadmap)
  const target = plan?.targetDirection || plan?.target_direction || plan?.direction || payload?.currentGoal?.title || ''

  return {
    id: plan?.id || 'active-growth-plan',
    slug: 'active-growth-plan',
    source: plan?.source === 'ai_generated' || Number(plan?.schemaVersion) >= 2 ? 'ai_growth_plan' : 'growth_plan',
    name: target ? `${target}学习路线` : '我的学习路线',
    category: 'growth_plan',
    description: roadmap[0]?.goal || '围绕你的成长计划继续推进学习、练习和项目任务。',
    targetJob: target,
    difficulty: plan?.stage || summary.current?.name || '基础到进阶',
    estimatedDays: Math.max(30, roadmap.length * 14),
    progress: summary.progress,
    totalNodes: roadmap.reduce((sum, stage) => sum + Number(stage.steps?.length || 0), 0),
    completedNodes: roadmap.reduce((sum, stage) => sum + Number(stage.completedSteps || 0), 0),
    todayTask: summary.currentStep
      ? {
          id: summary.currentStep.id,
          slug: summary.currentStep.id,
          title: summary.currentStep.title,
          description: summary.currentStep.exercise || summary.current?.goal || '',
          completed: summary.currentStep.completed,
          source: plan?.source === 'ai_generated' || Number(plan?.schemaVersion) >= 2 ? 'ai_growth_plan' : 'growth_plan',
        }
      : null,
    stages: roadmap.map((stage, index) => ({
      id: stage.id,
      title: stage.name,
      name: stage.name,
      description: stage.goal,
      orderNum: stage.order || index + 1,
      estimatedDays: Number.parseInt(stage.estimatedTime, 10) || 7,
      progress: stage.stepProgress || 0,
      completedNodes: stage.completedSteps || 0,
      totalNodes: stage.steps?.length || 0,
      nodes: (stage.steps || []).map((step) => ({
        id: step.id,
        slug: step.id,
        title: step.title,
        description: step.exercise || step.project || stage.goal,
        type: step.project ? 'practice' : 'document',
        duration: `${step.minutes || 30} 分钟`,
        completed: step.completed,
        source: plan?.source === 'ai_generated' || Number(plan?.schemaVersion) >= 2 ? 'ai_growth_plan' : 'growth_plan',
      })),
    })),
  }
}

function LearningRouteEmpty({ isLoggedIn, t }) {
  return (
    <main className="page-container student-roadmap-page learning-route-product-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to="/growth"><ArrowLeft size={16} /> {t('learningRoute.backGrowth')}</Link>
      </section>

      <section className="student-roadmap-hero learning-route-hero learning-route-empty-hero">
        <div>
          <span><Map size={18} /> {t('learningRoute.eyebrow')}</span>
          <h1>{t('learningRoute.emptyTitle')}</h1>
          <p>{t('learningRoute.emptyDesc')}</p>
          <div className="learning-route-empty-actions">
            <Link className="primary-button" to="/explore">
              {t('learningRoute.chooseDirection')} <ArrowRight size={16} />
            </Link>
            <Link className="secondary-button" to="/growth">
              {t('learningRoute.backGrowth')}
            </Link>
          </div>
        </div>
      </section>

      <section className="learning-route-guide">
        <article>
          <Target size={18} />
          <span>{t('learningRoute.guideDirection')}</span>
          <strong>{t('learningRoute.guideDirectionDesc')}</strong>
        </article>
        <article>
          <Clock3 size={18} />
          <span>{t('learningRoute.guideTask')}</span>
          <strong>{t('learningRoute.guideTaskDesc')}</strong>
        </article>
        <article>
          <BookOpenCheck size={18} />
          <span>{t('learningRoute.guideSaved')}</span>
          <strong>{t(isLoggedIn ? 'learningRoute.guideSavedDescLoggedIn' : 'learningRoute.guideSavedDesc')}</strong>
        </article>
      </section>

      <section className="learning-route-optional">
        <div>
          <span>{t('learningRoute.optionalTitle')}</span>
          <p>{t('learningRoute.optionalDesc')}</p>
        </div>
        <Link className="secondary-button" to="/learning/path/java-backend">
          {t('learningRoute.viewJavaRoute')}
        </Link>
      </section>
    </main>
  )
}

function LearningPage() {
  const { t } = useLanguage()
  const { user } = useAuth() || {}
  const params = useParams()
  const hasExplicitPath = Boolean(params.idOrSlug)
  const pathId = params.idOrSlug || DEFAULT_PATH_ID
  const [path, setPath] = useState(() => emptyPath(pathId))
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!hasExplicitPath) {
      const controller = new AbortController()
      setLoading(true)
      Promise.all([
        getLatestAiGrowthPlan(controller.signal).catch(() => ({ plan: null })),
        getStudentGrowthMe(controller.signal).catch(() => null),
      ])
        .then(([aiPayload, growthPayload]) => {
          const aiPlan = aiPayload?.plan
          const planPath = normalizePlanPath(aiPlan?.source === 'ai_generated' ? { plan: aiPlan } : growthPayload)
          if (planPath) {
            setPath(planPath)
            setError('')
          } else {
            setPath(emptyPath(pathId))
          }
        })
        .catch((err) => {
          if (err.name === 'AbortError') return
          setPath(emptyPath(pathId))
          setError('')
        })
        .finally(() => setLoading(false))
      return () => controller.abort()
    }
    const controller = new AbortController()
    setLoading(true)
    getLearningPath(pathId, controller.signal)
      .then((payload) => {
        setPath(normalizePath(payload, pathId))
        setError('')
      })
      .catch((err) => {
        if (err.name === 'AbortError') return
        setPath(emptyPath(pathId))
        setError(err.message || t('learningRoute.loadFailedDesc'))
      })
      .finally(() => setLoading(false))
    return () => controller.abort()
  }, [hasExplicitPath, pathId, t])

  const stages = useMemo(() => (Array.isArray(path.stages) ? path.stages : []), [path.stages])
  const allNodes = useMemo(() => stages.flatMap((stage) => Array.isArray(stage.nodes) ? stage.nodes : []), [stages])
  const todayTask = path.todayTask || allNodes.find((node) => !node.completed)
  const currentStage = useMemo(() => (
    stages.find((stage) => (stage.nodes || []).some((node) => !node.completed)) || stages[0] || null
  ), [stages])
  const nodeHref = (node) => node?.source === 'ai_growth_plan'
    ? `/learning/ai/${encodeURIComponent(node.slug || node.id)}`
    : `/learning/${encodeURIComponent(node?.slug || node?.id || '')}`

  if (!hasExplicitPath && !loading && path.slug === DEFAULT_PATH_ID && !path.stages.length) {
    return <LearningRouteEmpty t={t} isLoggedIn={Boolean(user)} />
  }

  return (
    <main className="page-container student-roadmap-page learning-route-product-page">
      <section className="student-roadmap-stage-toolbar">
        <Link className="secondary-button" to="/growth"><ArrowLeft size={16} /> {t('learningRoute.backGrowth')}</Link>
      </section>

      <section className="student-roadmap-hero learning-route-hero">
        <div>
          <span><Map size={18} /> {t('learningRoute.eyebrow')}</span>
          <h1>{loading ? t('learningRoute.loadingTitle') : path.name}</h1>
          <p>{path.description}</p>
          <Link className="primary-button" to={todayTask ? nodeHref(todayTask) : '/today'}>
            {todayTask ? t('learningRoute.continueLearning') : t('learningRoute.viewTodayTask')} <ArrowRight size={16} />
          </Link>
        </div>
        <div className="student-roadmap-progress">
          <strong>{loading ? <Loader2 size={22} /> : `${Number(path.progress || 0)}%`}</strong>
          <span>{t('learningRoute.nodeProgress', { done: Number(path.completedNodes || 0), total: Number(path.totalNodes || allNodes.length || 0) })}</span>
        </div>
      </section>

      {error ? (
        <div className="student-growth-error">
          <strong>{t('learningRoute.loadFailedTitle')}</strong>
          <p>{error}</p>
          <Link className="secondary-button" to="/growth">{t('learningRoute.backGrowth')}</Link>
        </div>
      ) : null}

      <section className="learning-route-summary-grid" aria-label={t('learningRoute.overviewAria')}>
        <article className="learning-route-summary-card">
          <span><Target size={16} /> {t('learningRoute.currentDirection')}</span>
          <strong>{path.targetJob || t('learningRoute.targetRole')}</strong>
          <p>{t('learningRoute.estimatedDays', { difficulty: path.difficulty || t('learningRoute.entryLevel'), days: path.estimatedDays || 120 })}</p>
          <Link className="secondary-button" to="/learning">{t('learningRoute.viewRouteDetail')}</Link>
        </article>
        <article className="learning-route-summary-card">
          <span><Clock3 size={16} /> {t('learningRoute.todayTask')}</span>
          <strong>{todayTask?.title || t('learningRoute.firstNodeTitle')}</strong>
          <p>{todayTask?.description || t('learningRoute.firstNodeDesc')}</p>
          <Link className="primary-button" to={todayTask ? nodeHref(todayTask) : '/today'}>
            {t('learningRoute.startTodayTask')} <ArrowRight size={16} />
          </Link>
        </article>
        <article className="learning-route-summary-card">
          <span><BookOpenCheck size={16} /> {t('learningRoute.currentStage')}</span>
          <strong>{currentStage?.title || currentStage?.name || t('learningRoute.waitingRouteData')}</strong>
          <p>{currentStage?.description || t('learningRoute.routeDataFallback')}</p>
          <Link className="secondary-button" to="/today">{t('learningRoute.viewTodayPlan')}</Link>
        </article>
      </section>

      <section className="learning-route-stage-list" aria-label={t('learningRoute.stageListAria')}>
        {loading ? (
          <article className="learning-route-stage-card is-current">
            <span><Loader2 size={16} /> {t('learningRoute.loadingStage')}</span>
            <h2>{t('learningRoute.loadingStageTitle')}</h2>
            <p>{t('learningRoute.loadingStageDesc')}</p>
          </article>
        ) : null}

        {!loading && stages.length === 0 ? (
          <article className="learning-route-stage-card is-current">
            <span>{t('learningRoute.emptyState')}</span>
            <h2>{t('learningRoute.noNodesTitle')}</h2>
            <p>{t('learningRoute.noNodesDesc')}</p>
            <Link className="primary-button" to="/growth">{t('learningRoute.backGrowth')}</Link>
          </article>
        ) : null}

        {stages.map((stage, index) => {
          const stageNodes = Array.isArray(stage.nodes) ? stage.nodes : []
          const isCurrent = currentStage?.id === stage.id
          const stageProgress = Number(stage.progress || 0)
          return (
            <article className={`learning-route-stage-card ${isCurrent ? 'is-current' : ''}`} key={stage.id || index}>
              <div className="learning-route-stage-head">
                <div>
                  <span>{t('learningRoute.stageLabel', { order: stage.orderNum || index + 1 })}</span>
                  <h2>{stage.title || stage.name || t('learningRoute.stageLabel', { order: index + 1 })}</h2>
                  <p>{stage.description || t('learningRoute.stageFallbackDesc')}</p>
                </div>
                <strong>{t('learningRoute.daysValue', { days: stage.estimatedDays || 7 })}</strong>
              </div>
              <div className="learning-route-stage-progress">
                <span>{t('learningRoute.stageProgress', { progress: stageProgress, done: Number(stage.completedNodes || 0), total: Number(stage.totalNodes || stageNodes.length || 0) })}</span>
                <i><b style={{ width: `${stageProgress}%` }} /></i>
              </div>
              <div className="learning-route-node-grid">
                {stageNodes.map((node) => {
                  const nodeKey = node.slug || node.id
                  return (
                    <Link className={`learning-route-node-card ${node.completed ? 'is-done' : ''}`} key={node.id} to={nodeHref({ ...node, slug: nodeKey })}>
                      <span>{typeLabel(node.type, t)}</span>
                      <strong>{node.title || t('learningRoute.learningNode')}</strong>
                      <small>{node.duration || t('learningRoute.defaultDuration')} · {nodeStatus(node, t)}</small>
                      <em>{node.completed ? t('learningRoute.view') : isCurrent ? t('learningRoute.continue') : t('learningRoute.start')} <ArrowRight size={14} /></em>
                    </Link>
                  )
                })}
              </div>
            </article>
          )
        })}
      </section>
    </main>
  )
}

export default LearningPage
